/**
 * Group delay line chart (ms) — mobile's light-themed Canvas2D counterpart
 * to web's `web/src/components/room/charts/phaseDraw.ts` (group-delay half).
 * Same geometry (`computeGroupDelayGeometry`), different ink — see
 * `./_shared.ts`'s module doc. Shares its pad with `./phase.ts`: both read
 * the same complex transfer function's phase, at the same 20-200 Hz window.
 */

import type { ChartPad } from '../layout';
import { computeGroupDelayGeometry } from '../phase';
import { setupCanvas, drawEmptyMessage, drawLogFreqAxisGrid, drawSeriesLine, INK } from './_shared';
import { PHASE_PAD } from './phase';

export const GROUP_DELAY_PAD: ChartPad = PHASE_PAD;

export interface DrawGroupDelayOpts {
  freqs: number[];
  phase: number[];
  width: number;
  height: number;
  fMin?: number;
  fMax?: number;
}

export function drawGroupDelayCanvas(canvas: HTMLCanvasElement, opts: DrawGroupDelayOpts): void {
  const s = setupCanvas(canvas, opts.width, opts.height);
  if (!s) return;
  const { ctx, W, H } = s;

  const geo = computeGroupDelayGeometry({
    freqs: opts.freqs,
    phase: opts.phase,
    width: opts.width,
    height: opts.height,
    pad: GROUP_DELAY_PAD,
    fMin: opts.fMin,
    fMax: opts.fMax,
    measureWidth: (label) => ctx.measureText(label).width,
  });

  if (geo.empty) {
    drawEmptyMessage(ctx, W, H, 'No frequency response data yet');
    return;
  }

  drawLogFreqAxisGrid(ctx, geo.frame, geo.xTicks, geo.yTicks);
  drawSeriesLine(ctx, geo.frame, geo.activePoints, INK.primary, 2.0);
}
